/* eslint-disable @typescript-eslint/no-explicit-any */
import type { StandardSchemaV1 } from '@standard-schema/spec';
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import humps, { type HumpsProcessor } from 'humps';
import isBoolean from 'lodash/isBoolean';
import { nextTick } from 'vue';

import { useIdentityStore } from '@/stores/identity';
import { IMPERSONATED_USER_ID_KEY, useImpersonatorStore } from '@/stores/impersonator';
import { useTokenStore } from '@/stores/token';
import type { AxiosOptions } from '@/types/axios';
import type { HttpClient, HttpClientOptions, HttpResponse } from '@/types/http';
import { browserStorageGetSessionItem } from '@/utils/browserStorage';
import { standardValidate } from '@/utils/standardValidate';

function normalizeResponse<T>(response: AxiosResponse<T>): HttpResponse<T> {
  return {
    data: response.data,
    status: response.status,
    headers: Object.fromEntries(Object.entries(response.headers)),
  };
}

function buildHttpClient(getAx: () => AxiosInstance): HttpClient {
  return {
    get: (url, config) => getAx().get(url, config).then(normalizeResponse),
    delete: (url, config) => getAx().delete(url, config).then(normalizeResponse),
    head: (url, config) => getAx().head(url, config).then(normalizeResponse),
    options: (url, config) => getAx().options(url, config).then(normalizeResponse),
    post: (url, data, config) => getAx().post(url, data, config).then(normalizeResponse),
    put: (url, data, config) => getAx().put(url, data, config).then(normalizeResponse),
    patch: (url, data, config) => getAx().patch(url, data, config).then(normalizeResponse),
  };
}

function logout() {
  useTokenStore().clearToken();
  useIdentityStore().clearCurrentBrand();
  useIdentityStore().clearIdentity();
  useImpersonatorStore().clearImpersonatedUser();
  nextTick(() => {
    window.location.assign('/logout');
  });
}

async function attemptTokenRefresh() {
  const success = await useTokenStore().refreshToken();
  if (success) {
    return true;
  }
  logout();
  return false;
}

async function attemptTokenRefreshIfBadToken(error: AxiosError) {
  const { status, data = {} as any } = error.response ?? {};
  if (status === 401) {
    // Checking if status is 401 should be enough to determine if we need to redirect to login,
    // but just in case, we check the error message to ensure we're only redirecting when token
    // is invalid or expired.
    const tokenErrors = [
      'Authentication token missing or invalid.',
      'Authentication token invalid or expired.', // Deprecated
      'Token Expired.', // Deprecated
      'Invalid Token.', // Deprecated
    ];
    if (tokenErrors.indexOf(data.description) > -1) {
      return attemptTokenRefresh();
    }
  }
  return false;
}

function createAxios(
  baseURL: string,
  {
    includeAuth = true,
    camelizeKeys = false,
    decamelizeRequests = true,
    decamelizeIgnoreKeys,
  }: AxiosOptions = {},
) {
  const ax = axios.create({ baseURL });

  ax.interceptors.request.use(
    (axConfig: InternalAxiosRequestConfig & AxiosOptions) => {
      const decamelizeKeys = isBoolean(axConfig?.decamelizeRequests)
        ? axConfig?.decamelizeRequests
        : decamelizeRequests;
      if (decamelizeKeys) {
        const decamelizeConvert: HumpsProcessor = (key, convert, convertOptions) =>
          decamelizeIgnoreKeys?.includes(key) ? key : convert(key, convertOptions);
        axConfig.params = humps.decamelizeKeys(axConfig.params, decamelizeConvert);
        axConfig.data = humps.decamelizeKeys(axConfig.data, decamelizeConvert);
      }
      if (includeAuth) {
        const token = useTokenStore().token;
        if (token && !axConfig.headers.Authorization) {
          axConfig.headers.Authorization = `Bearer ${token}`;
        }
      }
      const impersonatedUserId = browserStorageGetSessionItem(IMPERSONATED_USER_ID_KEY);
      if (impersonatedUserId) {
        axConfig.headers['X-On-Behalf-Of'] = impersonatedUserId;
      }
      return axConfig;
    },
    (error) => Promise.reject(error),
  );

  ax.interceptors.response.use(
    async (response) => {
      const requestConfig = response?.config as Pick<AxiosOptions, 'camelizeKeys'> & {
        schema?: StandardSchemaV1;
      };
      const shouldCamelizeKeys = isBoolean(requestConfig?.camelizeKeys)
        ? requestConfig?.camelizeKeys
        : camelizeKeys;
      const data = requestConfig?.schema
        ? await standardValidate(requestConfig.schema, response.data)
        : response.data;
      return {
        ...response,
        data: shouldCamelizeKeys ? humps.camelizeKeys(data) : data,
      };
    },
    async (error) => {
      const tokenStore = useTokenStore();
      if (tokenStore.token && !tokenStore.isShareToken && error.response) {
        // we do const { status, data } = error.response.
        // want to make sure error.response exists.
        const success = await attemptTokenRefreshIfBadToken(error);
        if (success) {
          // if token was refreshed successfully, retry the request
          error.response.config.headers.Authorization = `Bearer ${tokenStore.token}`;
          return new Promise((resolve) => {
            resolve(ax(error.response.config));
          });
        }
      }
      return Promise.reject(error);
    },
  );

  return ax;
}

/**
 * Creates an HTTP client for internal APIs with authentication, impersonation, and camelization.
 *
 * For external APIs, use createHttpClient from http.ts instead.
 */
export function createAxiosInstance(baseURL: string, options?: HttpClientOptions): HttpClient {
  let ax: AxiosInstance | null = null;
  const getAx = () => {
    ax ??= createAxios(baseURL, options);
    return ax;
  };
  return buildHttpClient(getAx);
}
